import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './PanelSettings.css';

const API_BASE = process.env.REACT_APP_API_BASE;

const GROUP_META = {
    roles: { label: '🎭 Rollen', icon: '🎭', color: '#7c3aed', description: 'Discord Rollen-IDs konfigurieren' },
    channels: { label: '📢 Channels', icon: '📢', color: '#0ea5e9', description: 'Discord Channel-IDs konfigurieren' },
    management: { label: '🛡️ Rollen-Verwaltung', icon: '🛡️', color: '#dc2626', description: 'Benutzer-Rollen & Discord-Mapping definieren' },
    discord: { label: '🤖 Discord Bot', icon: '🤖', color: '#5865F2', description: 'Discord Bot & OAuth2 Konfiguration' },
    urls: { label: '🔗 URLs', icon: '🔗', color: '#10b981', description: 'Server & Panel URLs konfigurieren' },
    general: { label: '⚙️ Allgemein', icon: '⚙️', color: '#f59e0b', description: 'Allgemeine Einstellungen' },
};

export default function PanelSettings({ hasPermission }) {
    const [settings, setSettings] = useState([]);
    const [grouped, setGrouped] = useState({});
    const [editedValues, setEditedValues] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saveStatus, setSaveStatus] = useState(null); // { type: 'success'|'error', message }
    const [activeGroup, setActiveGroup] = useState('roles');
    const [showPasswords, setShowPasswords] = useState({});
    const [searchQuery, setSearchQuery] = useState('');
    const [dirtyKeys, setDirtyKeys] = useState(new Set());

    // Discord Data State
    const [discordRoles, setDiscordRoles] = useState([]);
    const [discordChannels, setDiscordChannels] = useState([]);

    // State for Role Management
    const [roleDefinitions, setRoleDefinitions] = useState([]);
    const [editedRoles, setEditedRoles] = useState({});

    const loadSettings = useCallback(async () => {
        const token = localStorage.getItem('discord_token');
        setLoading(true);
        try {
            // Load standard settings
            const res = await axios.get(`${API_BASE}/api/panel-settings`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data.success) {
                setSettings(res.data.settings);
                setGrouped(res.data.grouped);
                const vals = {};
                res.data.settings.forEach(s => { vals[s.key] = s.value || ''; });
                setEditedValues(vals);
                setDirtyKeys(new Set());
            }

            // Load role definitions
            const rolesRes = await axios.get(`${API_BASE}/api/panel-settings/config/user-roles`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (rolesRes.data.success) {
                setRoleDefinitions(rolesRes.data.roles);
                const roleVals = {};
                rolesRes.data.roles.forEach(r => {
                    roleVals[r.id] = { ...r };
                });
                setEditedRoles(roleVals);
            }

        } catch (err) {
            console.error('Error loading panel settings:', err);
            setSaveStatus({ type: 'error', message: 'Einstellungen konnten nicht geladen werden.' });
        } finally {
            setLoading(false);
        }
    }, []);

    const loadDiscordData = useCallback(async () => {
        const token = localStorage.getItem('discord_token');
        try {
            const [rolesRes, channelsRes] = await Promise.all([
                axios.get(`${API_BASE}/api/panel-settings/discord/roles`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { roles: [] } })),
                axios.get(`${API_BASE}/api/panel-settings/discord/channels`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { channels: [] } }))
            ]);
            if (rolesRes.data?.roles) setDiscordRoles(rolesRes.data.roles.sort((a, b) => a.name.localeCompare(b.name)));
            if (channelsRes.data?.channels) setDiscordChannels(channelsRes.data.channels.sort((a, b) => {
                const parentA = a.parentName || '';
                const parentB = b.parentName || '';
                if (parentA !== parentB) return parentA.localeCompare(parentB);
                return a.name.localeCompare(b.name);
            }));
        } catch (err) {
            console.error('Failed to load discord data', err);
        }
    }, []);

    useEffect(() => { 
        loadSettings(); 
        loadDiscordData();
    }, [loadSettings, loadDiscordData]);

    const handleChange = (key, value) => {
        setEditedValues(prev => ({ ...prev, [key]: value }));
        const original = settings.find(s => s.key === key);
        if (original && original.value !== value) {
            setDirtyKeys(prev => new Set([...prev, key]));
        } else {
            setDirtyKeys(prev => {
                const next = new Set(prev);
                next.delete(key);
                return next;
            });
        }
    };

    const handleRoleChange = (roleId, field, value) => {
        setEditedRoles(prev => ({
            ...prev,
            [roleId]: { ...prev[roleId], [field]: value }
        }));
    };

    const handleSaveRole = async (roleId) => {
        const token = localStorage.getItem('discord_token');
        setSaving(true);
        setSaveStatus(null);
        try {
            const roleData = editedRoles[roleId];
            const res = await axios.put(`${API_BASE}/api/panel-settings/config/user-roles/${roleId}`, roleData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data.success) {
                setSaveStatus({ type: 'success', message: res.data.message });
                await loadSettings();
            }
        } catch (err) {
            setSaveStatus({ type: 'error', message: err.response?.data?.error || 'Fehler beim Speichern der Rolle.' });
        } finally {
            setSaving(false);
            setTimeout(() => setSaveStatus(null), 3000);
        }
    };

    const handleSaveAll = async () => {
        if (dirtyKeys.size === 0) return;
        const token = localStorage.getItem('discord_token');
        setSaving(true);
        setSaveStatus(null);
        try {
            const toSave = [...dirtyKeys].map(key => ({
                key,
                value: editedValues[key]
            }));
            const res = await axios.post(`${API_BASE}/api/panel-settings/bulk`, { settings: toSave }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data.success) {
                setSaveStatus({ type: 'success', message: res.data.message });
                await loadSettings();
            }
        } catch (err) {
            setSaveStatus({ type: 'error', message: err.response?.data?.error || 'Fehler beim Speichern.' });
        } finally {
            setSaving(false);
            setTimeout(() => setSaveStatus(null), 4000);
        }
    };

    const handleSaveSingle = async (key) => {
        const token = localStorage.getItem('discord_token');
        setSaving(true);
        setSaveStatus(null);
        try {
            const res = await axios.post(`${API_BASE}/api/panel-settings`, {
                key,
                value: editedValues[key]
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data.success) {
                setSaveStatus({ type: 'success', message: res.data.message });
                setDirtyKeys(prev => {
                    const next = new Set(prev);
                    next.delete(key);
                    return next;
                });
                await loadSettings();
            }
        } catch (err) {
            setSaveStatus({ type: 'error', message: err.response?.data?.error || 'Fehler beim Speichern.' });
        } finally {
            setSaving(false);
            setTimeout(() => setSaveStatus(null), 4000);
        }
    };

    const handleSystemAction = async (action) => {
        const confirmMsg = action === 'restart' 
            ? 'Möchtest du den Bot/Server wirklich neustarten?' 
            : 'Möchtest du den React-Build (npm run build) wirklich starten? Dies kann einige Minuten dauern.';
            
        if (!window.confirm(confirmMsg)) return;

        const token = localStorage.getItem('discord_token');
        setSaving(true);
        setSaveStatus(null);
        try {
            const res = await axios.post(`${API_BASE}/api/panel-settings/system-action`, { action }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data.success) {
                setSaveStatus({ type: 'success', message: res.data.message });
            }
        } catch (err) {
            setSaveStatus({ type: 'error', message: err.response?.data?.error || 'Fehler beim Ausführen der Aktion.' });
        } finally {
            setSaving(false);
            if (action !== 'restart') {
                setTimeout(() => setSaveStatus(null), 8000);
            }
        }
    };

    const toggleShowPassword = (key) => {
        setShowPasswords(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const getFilteredSettings = () => {
        const groupSettings = grouped[activeGroup] || [];
        if (!searchQuery) return groupSettings;
        const q = searchQuery.toLowerCase();
        return settings.filter(s =>
            s.label?.toLowerCase().includes(q) ||
            s.key?.toLowerCase().includes(q) ||
            s.value?.toLowerCase().includes(q)
        );
    };

    const filteredSettings = getFilteredSettings();
    const activeGroupMeta = GROUP_META[activeGroup] || GROUP_META.general;

    if (loading) {
        return (
            <div className="panel-settings-container">
                <div className="ps-loading">
                    <div className="spinner" />
                    <p>Einstellungen werden geladen...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="panel-settings-container">
            {/* Header */}
            <div className="ps-header">
                <div className="ps-header-left">
                    <div className="ps-header-icon">⚙️</div>
                    <div>
                        <h2 className="ps-title">Panel Settings</h2>
                        <p className="ps-subtitle">Rollen, Channels und Bot-Konfiguration verwalten</p>
                    </div>
                </div>
                <div className="ps-header-actions">
                    {dirtyKeys.size > 0 && (
                        <span className="ps-dirty-badge">{dirtyKeys.size} ungespeichert</span>
                    )}
                    <button
                        className="ps-btn ps-btn-primary"
                        onClick={handleSaveAll}
                        disabled={saving || dirtyKeys.size === 0}
                    >
                        {saving ? '⏳ Speichert...' : `💾 Alle speichern${dirtyKeys.size > 0 ? ` (${dirtyKeys.size})` : ''}`}
                    </button>
                </div>
            </div>

            {/* Status Alert */}
            {saveStatus && (
                <div className={`ps-alert ps-alert-${saveStatus.type}`}>
                    {saveStatus.type === 'success' ? '✅' : '❌'} {saveStatus.message}
                </div>
            )}

            {/* Search Bar - Hide when in Role Management */}
            {activeGroup !== 'management' && (
                <div className="ps-search-bar">
                    <span className="ps-search-icon">🔍</span>
                    <input
                        type="text"
                        placeholder="Einstellung suchen (z.B. LOG_CHANNEL_ID, Admin Rolle...)"
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        className="ps-search-input"
                    />
                    {searchQuery && (
                        <button className="ps-search-clear" onClick={() => setSearchQuery('')}>✕</button>
                    )}
                </div>
            )}

            <div className="ps-layout">
                {/* Sidebar Navigation */}
                <nav className="ps-sidebar">
                    {Object.entries(GROUP_META).map(([groupKey, meta]) => {
                        const groupSettings = grouped[groupKey] || [];
                        const dirtyCount = groupSettings.filter(s => dirtyKeys.has(s.key)).length;
                        return (
                            <button
                                key={groupKey}
                                className={`ps-nav-item ${activeGroup === groupKey && !searchQuery ? 'active' : ''}`}
                                onClick={() => { setActiveGroup(groupKey); setSearchQuery(''); }}
                            >
                                <span className="ps-nav-icon">{meta.icon}</span>
                                <span className="ps-nav-label">{meta.label}</span>
                                <span className="ps-nav-count">
                                    {groupKey === 'management' ? roleDefinitions.length : groupSettings.length}
                                </span>
                                {dirtyCount > 0 && (
                                    <span className="ps-nav-dirty">{dirtyCount}</span>
                                )}
                            </button>
                        );
                    })}
                </nav>

                {/* Content Area */}
                <div className="ps-content">
                    {searchQuery ? (
                        <div className="ps-group-header">
                            <p className="ps-group-desc">🔍 Suchergebnisse für „{searchQuery}" — {filteredSettings.length} gefunden</p>
                        </div>
                    ) : (
                        <div className="ps-group-header" style={{ borderLeftColor: activeGroupMeta.color }}>
                            <h3 className="ps-group-title">{activeGroupMeta.label}</h3>
                            <p className="ps-group-desc">{activeGroupMeta.description}</p>
                        </div>
                    )}

                    {/* Conditional Rendering: Role Management Tab */}
                    {activeGroup === 'management' ? (
                        <div className="ps-roles-management">
                            <div className="ps-roles-actions-top">
                                <button 
                                    className="ps-btn ps-btn-primary"
                                    onClick={async () => {
                                        const token = localStorage.getItem('discord_token');
                                        try {
                                            // Simple way to "add": POST then reload
                                            await axios.post(`${API_BASE}/api/panel-settings/config/user-roles`, {
                                                name: 'Neue Rolle',
                                                discord_role_id: '',
                                                description: 'Beschreibung hier...'
                                            }, { headers: { Authorization: `Bearer ${token}` }});
                                            await loadSettings();
                                        } catch (e) {
                                            setSaveStatus({ type: 'error', message: 'Fehler beim Erstellen' });
                                        }
                                    }}
                                >
                                    ➕ Neue Rolle hinzufügen
                                </button>
                            </div>

                            <table className="ps-roles-table">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Rollen-Name (Panel)</th>
                                        <th>Discord Rollen-ID</th>
                                        <th>Beschreibung</th>
                                        <th>Aktionen</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {roleDefinitions.map(role => (
                                        <tr key={role.id}>
                                            <td className="ps-role-id">#{role.id}</td>
                                            <td>
                                                <input 
                                                    type="text" 
                                                    className="ps-input-small"
                                                    value={editedRoles[role.id]?.name || ''}
                                                    onChange={(e) => handleRoleChange(role.id, 'name', e.target.value)}
                                                />
                                            </td>
                                            <td>
                                                {discordRoles.length > 0 ? (
                                                    <select 
                                                        className="ps-input-small ps-code-font"
                                                        value={editedRoles[role.id]?.discord_role_id || ''}
                                                        onChange={(e) => handleRoleChange(role.id, 'discord_role_id', e.target.value)}
                                                    >
                                                        <option value="">-- Rolle wählen --</option>
                                                        {discordRoles.map(r => (
                                                            <option key={r.id} value={r.id}>{r.name} ({r.id})</option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <input 
                                                        type="text" 
                                                        className="ps-input-small ps-code-font"
                                                        value={editedRoles[role.id]?.discord_role_id || ''}
                                                        onChange={(e) => handleRoleChange(role.id, 'discord_role_id', e.target.value)}
                                                        placeholder="z.B. 123456789..."
                                                    />
                                                )}
                                            </td>
                                            <td>
                                                <input 
                                                    type="text" 
                                                    className="ps-input-small"
                                                    value={editedRoles[role.id]?.description || ''}
                                                    onChange={(e) => handleRoleChange(role.id, 'description', e.target.value)}
                                                />
                                            </td>
                                            <td className="ps-role-actions-cell">
                                                <button 
                                                    className="ps-btn ps-btn-save-mini"
                                                    onClick={() => handleSaveRole(role.id)}
                                                    disabled={saving}
                                                    title="Speichern"
                                                >
                                                    💾
                                                </button>
                                                {role.id !== 1 && (
                                                    <button 
                                                        className="ps-btn ps-btn-delete-mini"
                                                        onClick={async () => {
                                                            if (!window.confirm(`Rolle "${role.name}" wirklich löschen?`)) return;
                                                            const token = localStorage.getItem('discord_token');
                                                            try {
                                                                await axios.delete(`${API_BASE}/api/panel-settings/config/user-roles/${role.id}`, {
                                                                    headers: { Authorization: `Bearer ${token}` }
                                                                });
                                                                await loadSettings();
                                                            } catch (e) {
                                                                setSaveStatus({ type: 'error', message: 'Fehler beim Löschen' });
                                                            }
                                                        }}
                                                        disabled={saving}
                                                        title="Löschen"
                                                    >
                                                        🗑️
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <div className="ps-info-box" style={{ marginTop: '20px' }}>
                                <span className="ps-info-icon">⚠️</span>
                                <div>
                                    <strong>Wichtig:</strong> Das Panel verwendet diese IDs, um Benutzer automatisch den richtigen Gruppen zuzuweisen. 
                                    Die Rolle <strong>➔ | Panel Admin</strong> (ID 1) hat immer vollen Zugriff auf diesen Bereich.
                                </div>
                            </div>
                        </div>
                    ) : (
                        /* Standard Settings Grid */
                        <div className="ps-settings-grid">
                            {filteredSettings.length === 0 ? (
                                <div className="ps-empty">
                                    <div className="ps-empty-icon">📭</div>
                                    <p>Keine Einstellungen gefunden</p>
                                </div>
                            ) : (
                                filteredSettings.map(setting => {
                                    const isDirty = dirtyKeys.has(setting.key);
                                    const isPassword = setting.type === 'password';
                                    const showPw = showPasswords[setting.key];

                                    return (
                                        <div key={setting.key} className={`ps-setting-card ${isDirty ? 'dirty' : ''}`}>
                                            <div className="ps-setting-header">
                                                <div>
                                                    <label className="ps-setting-label">{setting.label}</label>
                                                    <code className="ps-setting-key">{setting.key}</code>
                                                </div>
                                                <div className="ps-setting-badges">
                                                    {setting.inDatabase && (
                                                        <span className="ps-badge ps-badge-db" title="Gespeichert in Datenbank">💾 DB</span>
                                                    )}
                                                    {!setting.inDatabase && setting.value && (
                                                        <span className="ps-badge ps-badge-env" title="Aus .env geladen">.env</span>
                                                    )}
                                                    {isDirty && (
                                                        <span className="ps-badge ps-badge-dirty">● Geändert</span>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="ps-setting-input-row">
                                                {setting.type === 'textarea' ? (
                                                    <textarea
                                                        className="ps-input ps-textarea"
                                                        value={editedValues[setting.key] || ''}
                                                        onChange={e => handleChange(setting.key, e.target.value)}
                                                        placeholder={`${setting.key} eingeben...`}
                                                        rows={3}
                                                    />
                                                ) : setting.group === 'roles' && setting.key !== 'SUPER_ADMIN_USER' && discordRoles.length > 0 ? (
                                                    <select
                                                        className="ps-input"
                                                        value={editedValues[setting.key] || ''}
                                                        onChange={e => handleChange(setting.key, e.target.value)}
                                                    >
                                                        <option value="">-- Keine Rolle ausgewählt --</option>
                                                        {discordRoles.map(r => (
                                                            <option key={r.id} value={r.id}>{r.name} ({r.id})</option>
                                                        ))}
                                                    </select>
                                                ) : setting.group === 'channels' && discordChannels.length > 0 ? (
                                                    <select
                                                        className="ps-input"
                                                        value={editedValues[setting.key] || ''}
                                                        onChange={e => handleChange(setting.key, e.target.value)}
                                                    >
                                                        <option value="">-- Kein Channel ausgewählt --</option>
                                                        {discordChannels.map(c => (
                                                            <option key={c.id} value={c.id}>{c.parentName ? `${c.parentName} / ` : ''}{c.name} ({c.id})</option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <div className="ps-input-wrapper">
                                                        <input
                                                            type={isPassword && !showPw ? 'password' : 'text'}
                                                            className="ps-input"
                                                            value={editedValues[setting.key] || ''}
                                                            onChange={e => handleChange(setting.key, e.target.value)}
                                                            placeholder={`${setting.key} eingeben...`}
                                                            autoComplete="off"
                                                        />
                                                        {isPassword && (
                                                            <button
                                                                type="button"
                                                                className="ps-toggle-pw"
                                                                onClick={() => toggleShowPassword(setting.key)}
                                                                title={showPw ? 'Verbergen' : 'Anzeigen'}
                                                            >
                                                                {showPw ? '🙈' : '👁️'}
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                                {isDirty && (
                                                    <button
                                                        className="ps-btn ps-btn-save-single"
                                                        onClick={() => handleSaveSingle(setting.key)}
                                                        disabled={saving}
                                                        title="Nur diesen Wert speichern"
                                                    >
                                                        💾
                                                    </button>
                                                )}
                                            </div>

                                            {setting.updated_at && (
                                                <p className="ps-setting-meta">
                                                    Zuletzt geändert am {new Date(setting.updated_at).toLocaleDateString('de-DE', {
                                                        day: '2-digit', month: '2-digit', year: 'numeric',
                                                        hour: '2-digit', minute: '2-digit'
                                                    })}
                                                    {setting.updated_by && ` von ${setting.updated_by}`}
                                                </p>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    )}

                    {/* Spezielle System-Aktionen für "Allgemein" */}
                    {activeGroup === 'general' && !searchQuery && (
                        <div className="ps-settings-grid" style={{ marginTop: '30px' }}>
                            <div className="ps-setting-card">
                                <div className="ps-setting-header">
                                    <div>
                                        <label className="ps-setting-label">Frontend Build generieren</label>
                                        <p className="ps-setting-meta" style={{ marginTop: '5px' }}>Führt "npm run build" aus, um die Änderungen im Frontend für den Live-Betrieb (Production) zu übernehmen.</p>
                                    </div>
                                </div>
                                <div className="ps-setting-input-row" style={{ marginTop: '15px' }}>
                                    <button 
                                        className="ps-btn ps-btn-primary" 
                                        onClick={() => handleSystemAction('build')}
                                        disabled={saving}
                                    >
                                        📦 npm run build ausführen
                                    </button>
                                </div>
                            </div>
                            
                            <div className="ps-setting-card" style={{ borderLeft: '4px solid #ef4444' }}>
                                <div className="ps-setting-header">
                                    <div>
                                        <label className="ps-setting-label">Bot / Server neustarten</label>
                                        <p className="ps-setting-meta" style={{ marginTop: '5px' }}>Beendet den aktuellen Node.js Prozess. Wenn du PM2, Nodemon oder Pterodactyl nutzt, startet der Server automatisch von selbst wieder (Auto-Restart).</p>
                                    </div>
                                </div>
                                <div className="ps-setting-input-row" style={{ marginTop: '15px' }}>
                                    <button 
                                        className="ps-btn" 
                                        style={{ backgroundColor: '#ef4444', color: 'white' }}
                                        onClick={() => handleSystemAction('restart')}
                                        disabled={saving}
                                    >
                                        🔄 Neustart erzwingen
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Info Box */}
                    <div className="ps-info-box">
                        <span className="ps-info-icon">ℹ️</span>
                        <div>
                            <strong>Hinweis:</strong> Werte werden in der Datenbank gespeichert und überschreiben die <code>.env</code>-Datei zur Laufzeit.
                            Nach einem Server-Neustart werden zunächst wieder die <code>.env</code>-Werte geladen — sofern kein Datenbank-Eintrag vorhanden ist.
                            Werte mit dem Badge <strong>.env</strong> wurden noch nicht in die Datenbank übertragen.
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
